import { User } from "@core/account/types";
import { AUTH_TOKEN_KEY } from "@core/account/utils";
import {
  getSafeAuthReturnPath,
  parseError,
  safeSessionStorage,
  URLS,
} from "@core/shared/utils";
import { errorToast, successToast } from "@design-system/components/toast";
import cache from "@mongez/cache";
import events from "@mongez/events";
import { trans } from "@mongez/localization";
import { resetPasswordAtom } from "../atoms";

export const authEvents = {
  onLogin(callback: () => void) {
    return events.subscribe("user.loggedIn", callback);
  },
  triggerLogin() {
    events.trigger("user.loggedIn");
  },
};

export const goBack = (navigate) => {
  setTimeout(() => {
    navigate ? navigate(-1) : window.history.back();
  }, 0);
};

const getPreviousPagePath = (): string => {
  const returnTo = new URLSearchParams(window.location.search).get("returnTo");
  const requestedPath = getSafeAuthReturnPath(returnTo);

  if (requestedPath) return requestedPath;

  try {
    const navigationHistory = JSON.parse(
      safeSessionStorage.getItem("navigation_history") || "[]",
    ) as Array<{ path?: string }>;

    for (let index = navigationHistory.length - 1; index >= 0; index -= 1) {
      const previousPath = getSafeAuthReturnPath(
        navigationHistory[index]?.path,
      );

      if (previousPath) return previousPath;
    }
  } catch {
    // A malformed history entry should not prevent a successful login.
  }

  return URLS.home;
};

export const onSuccessLogin = (user: User) => {
  successToast(trans("auth.loginSuccess"));

  // Trigger login event
  authEvents.triggerLogin();

  cache.set(AUTH_TOKEN_KEY, user);

  // Use window.location.href for a full page reload to ensure server-client sync
  // This prevents the race condition where userAtom hasn't been updated yet
  // The server-side will have the correct user cookie, and the client will get the updated state
  window.location.href = user.requiresPasskeyEnrollment
    ? `${URLS.account.security}?passkeyRequired=1`
    : getPreviousPagePath();
};

export const onSuccessRegister = (navigate, user: User) => {
  successToast(trans("auth.registerSuccess"));

  cache.set(AUTH_TOKEN_KEY, user);

  navigate(
    user.requiresPasskeyEnrollment
      ? `${URLS.account.security}?passkeyRequired=1`
      : URLS.home,
  );
};

export const onSuccessVerifyRegister = (navigate, user: User) => {
  successToast(trans("auth.verifyOtpSuccess"));

  cache.set(AUTH_TOKEN_KEY, user);

  navigate(
    user.requiresPasskeyEnrollment
      ? `${URLS.account.security}?passkeyRequired=1`
      : URLS.home,
  );
};

export const onSuccessForgetPassword = (navigate, values, form) => {
  form.submitting(false);
  form.reset();

  resetPasswordAtom.merge({
    email: values.email,
  });
  successToast(trans("auth.sendOtpSuccess"));
  navigate(URLS.auth.verifyCode);
};

export const onSuccessVerifyForgetPassword = (navigate, values) => {
  resetPasswordAtom.change("code", values.code);
  localStorage.setItem("forgetPasswordCode", values.code);

  successToast(trans("auth.verifyOtpSuccess"));
  navigate(URLS.auth.resetPassword);
};

export const onSuccessResetPassword = (navigate) => {
  navigate(URLS.auth.login);
  successToast(trans("auth.resetPasswordSuccess"));

  resetPasswordAtom.reset();
  localStorage.removeItem("forgetPasswordCode");
  localStorage.removeItem("forgetPasswordEmail");
};

export const onFailedAuth = (error, form) => {
  form.submitting(false);
  errorToast(parseError(error));
};
